import { AppLayout } from "@bcc/components/app-layout";
import { DEFAULT_ORDER_STATUS, ORDER_STATUS_FILTERS } from "@bcc/components/orders/constants";
import { DemoDataTag } from "@bcc/components/tour/demo-data/demo-data-tag";
import { useDemoOrders } from "@bcc/components/tour/demo-data/use-demo-orders";
import { OrdersView } from "@bcc/components/views/orders-view";
import { useBatchesContext } from "@bcc/contexts/batches-context";
import { useOrderStatusCounts } from "@bcc/hooks/use-order-status-counts";
import { useOrdersInitialization } from "@bcc/hooks/use-orders-initialization";
import { usePurchaseOrdersState } from "@client-shared/providers/purchase-orders";
import { useVendorContext } from "@client-shared/providers/vendor";
import { decodeCustomerAccountNumbers } from "@client-shared/utilities/customerAccountUtilities";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

type OrdersPageParams = {
  carrierAccountId?: string;
  purchaseOrderId?: string;
};

export function OrdersPage() {
  const [state, , { initialize, clearCache, fetchPurchaseOrder, clearPurchaseOrder, setFetchEnabled }] = usePurchaseOrdersState();
  const [{ initialized: isVendorInitialized, customerAccountCarrierDetails, currentVendor }] = useVendorContext();
  const [, , { fetchBatches }] = useBatchesContext();
  const { carrierAccountId, purchaseOrderId } = useParams<OrdersPageParams>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const customerAccountNumbers = useMemo<string[] | null>(() => {
    if (!carrierAccountId) return [];
    try {
      return decodeCustomerAccountNumbers(carrierAccountId);
    } catch {
      return null;
    }
  }, [carrierAccountId]);

  const { filterCounts, isCountsLoading } = useOrderStatusCounts(currentVendor?.vendorKey, customerAccountNumbers ?? []);

  // Skip the orders fetch when the active filter is known to be empty.
  useEffect(() => {
    const orderStatusParam = searchParams.get("orderStatus") ?? DEFAULT_ORDER_STATUS;
    const filterKey =
      ORDER_STATUS_FILTERS.find((filter) => [...filter.statuses].join(",") === orderStatusParam)?.key ?? DEFAULT_ORDER_STATUS;
    const count = filterCounts[filterKey];
    setFetchEnabled(count !== 0);
  }, [filterCounts, searchParams, setFetchEnabled]);

  useOrdersInitialization({
    isVendorInitialized,
    carrierAccountId,
    purchaseOrderId,
    customerAccountNumbers,
    customerAccountCarrierDetails,
    searchParams,
    navigate,
    initialize,
  });

  // Sample orders replace the roaster's while the tour runs
  const demoOrders = useDemoOrders(searchParams.get("orderStatus"));
  const ordersSource = demoOrders
    ? {
        orders: demoOrders.orders,
        purchaseOrder: null,
        isLoading: demoOrders.isLoading,
        statusCounts: demoOrders.statusCounts,
        isStatusCountsLoading: demoOrders.isLoading,
        defaultActiveStatuses: DEFAULT_ORDER_STATUS,
      }
    : {
        orders: state.purchaseOrders ?? [],
        purchaseOrder: state.purchaseOrder ?? null,
        isLoading: !state.isInitialized || Boolean(state.isLoading) || Boolean(state.isFetching),
        statusCounts: filterCounts,
        isStatusCountsLoading: isCountsLoading,
        defaultActiveStatuses: undefined,
      };

  useEffect(() => {
    if (!purchaseOrderId || demoOrders) return;
    void fetchPurchaseOrder(purchaseOrderId);
  }, [purchaseOrderId, fetchPurchaseOrder, demoOrders]);

  return (
    <AppLayout>
      <DemoDataTag />
      <OrdersView
        orders={ordersSource.orders}
        purchaseOrder={ordersSource.purchaseOrder}
        isLoading={ordersSource.isLoading}
        clearCache={clearCache}
        refetchBatches={fetchBatches}
        onDeselectOrder={clearPurchaseOrder}
        statusCounts={ordersSource.statusCounts}
        isStatusCountsLoading={ordersSource.isStatusCountsLoading}
        defaultActiveStatuses={ordersSource.defaultActiveStatuses}
        purchaseOrderId={purchaseOrderId}
      />
    </AppLayout>
  );
}
